import {
  EFFECTIVENESS_TABLE,
  TECHNIQUES,
  defenseConfigKey,
  getShortcutAction,
  shouldIgnoreGlobalShortcutTarget,
  type DefenseConfig,
} from '../../lib/labs/jailbreak-sandbox';

// ============================================
// UI Logic
// ============================================
const root = document.querySelector('[data-jailbreak-sandbox]') as HTMLElement | null;

if (root) {
  // DOM Elements
  const runTestBtn = root.querySelector('#run-test') as HTMLButtonElement;
  const resetTestBtn = root.querySelector('#reset-test') as HTMLButtonElement;
  const testHint = root.querySelector('#test-hint') as HTMLElement;
  const testResult = root.querySelector('#test-result') as HTMLElement;
  const resultTechnique = root.querySelector('#result-technique') as HTMLElement;
  const resultDefenses = root.querySelector('#result-defenses') as HTMLElement;
  const resultResponse = root.querySelector('#result-response code') as HTMLElement;
  const resultIndicator = root.querySelector('#result-indicator') as HTMLElement;
  const resultIndicatorText = root.querySelector('#result-indicator-text') as HTMLElement;
  const resultScoreBar = root.querySelector('#result-score-bar') as HTMLElement;
  const resultScoreValue = root.querySelector('#result-score-value') as HTMLElement;
  const resultExplanation = root.querySelector('#result-explanation') as HTMLElement;
  const defenseStatus = root.querySelector('#defense-status') as HTMLElement;

  // Defense checkboxes
  const defenseIH = root.querySelector(
    '#defense-instruction-hierarchy'
  ) as HTMLInputElement;
  const defenseIS = root.querySelector('#defense-input-sanitization') as HTMLInputElement;
  const defenseOF = root.querySelector('#defense-output-filtering') as HTMLInputElement;
  const defenseCA = root.querySelector('#defense-constitutional-ai') as HTMLInputElement;

  // State
  let selectedTechnique: string | null = null;
  let defenseConfig: DefenseConfig = {
    instructionHierarchy: false,
    inputSanitization: false,
    outputFiltering: false,
    constitutionalAi: false,
  };

  // Technique selection
  const techniqueCards = root.querySelectorAll('.technique-card');

  function selectTechnique(id: string) {
    selectedTechnique = id;
    runTestBtn.disabled = false;
    techniqueCards.forEach((card) => {
      const cardId = card.getAttribute('data-technique');
      const isSelected = cardId === id;
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  techniqueCards.forEach((card) => {
    card.addEventListener('click', () => {
      const id = card.getAttribute('data-technique');
      if (id) selectTechnique(id);
    });
  });

  // Defense listeners
  function updateDefenseConfig() {
    defenseConfig = {
      instructionHierarchy: defenseIH.checked,
      inputSanitization: defenseIS.checked,
      outputFiltering: defenseOF.checked,
      constitutionalAi: defenseCA.checked,
    };
    updateDefenseStatus();
  }

  function updateDefenseStatus() {
    const active = Object.values(defenseConfig).filter(Boolean).length;
    if (active === 0) {
      defenseStatus.textContent = 'All defenses off';
      defenseStatus.classList.remove('border-emerald-500/50', 'text-emerald-500');
      defenseStatus.classList.add('border-border/70', 'text-muted');
    } else {
      defenseStatus.textContent = `${active} defense${active > 1 ? 's' : ''} active`;
      defenseStatus.classList.remove('border-border/70', 'text-muted');
      defenseStatus.classList.add('border-emerald-500/50', 'text-emerald-500');
    }
  }

  defenseIH?.addEventListener('change', updateDefenseConfig);
  defenseIS?.addEventListener('change', updateDefenseConfig);
  defenseOF?.addEventListener('change', updateDefenseConfig);
  defenseCA?.addEventListener('change', updateDefenseConfig);

  // Defense config description for display
  function getDefenseDescription(): string {
    const active: string[] = [];
    if (defenseConfig.instructionHierarchy) active.push('Instruction Hierarchy');
    if (defenseConfig.inputSanitization) active.push('Input Sanitization');
    if (defenseConfig.outputFiltering) active.push('Output Filtering');
    if (defenseConfig.constitutionalAi) active.push('Constitutional AI');
    return active.length > 0 ? active.join(', ') : 'None (all defenses off)';
  }

  // Run test
  function runTest() {
    if (!selectedTechnique) return;

    const key = defenseConfigKey(defenseConfig);
    const result = EFFECTIVENESS_TABLE[selectedTechnique]?.[key];
    if (!result) return;

    const technique = TECHNIQUES.find((t) => t.id === selectedTechnique);
    if (!technique) return;

    // Show result area, hide hint
    testHint?.classList.add('hidden');
    testResult.classList.remove('hidden');

    // Populate results
    resultTechnique.textContent = technique.title;
    resultDefenses.textContent = getDefenseDescription();
    resultResponse.textContent = result.response;

    // Success/fail indicator
    resultIndicator.classList.remove('result-success', 'result-fail');
    if (result.success) {
      resultIndicator.classList.add('result-success');
      resultIndicatorText.textContent = 'Jailbreak Succeeded';
    } else {
      resultIndicator.classList.add('result-fail');
      resultIndicatorText.textContent = 'Jailbreak Blocked';
    }

    // Score bar
    resultScoreBar.style.width = `${result.score}%`;
    if (result.score >= 50) {
      resultScoreBar.style.background = 'hsl(0 72% 50%)';
    } else if (result.score >= 20) {
      resultScoreBar.style.background = 'hsl(45 90% 50%)';
    } else {
      resultScoreBar.style.background = 'hsl(142 70% 45%)';
    }
    resultScoreValue.textContent = `${result.score}/100`;

    // Explanation
    resultExplanation.textContent = result.explanation;
  }

  function resetTest() {
    testResult.classList.add('hidden');
    testHint?.classList.remove('hidden');
    selectedTechnique = null;
    runTestBtn.disabled = true;
    techniqueCards.forEach((card) => {
      card.setAttribute('aria-pressed', 'false');
    });

    // Reset defenses
    defenseIH.checked = false;
    defenseIS.checked = false;
    defenseOF.checked = false;
    defenseCA.checked = false;
    updateDefenseConfig();
  }

  runTestBtn?.addEventListener('click', runTest);
  resetTestBtn?.addEventListener('click', resetTest);

  // Populate effectiveness matrix
  function populateMatrix() {
    const table = root.querySelector('#effectiveness-matrix') as HTMLTableElement;
    if (!table) return;

    const thead = table.querySelector('thead tr') as HTMLTableRowElement;
    const tbody = table.querySelector('tbody') as HTMLTableSectionElement;

    // Column configs to show: a representative subset
    const matrixConfigs = [
      { key: '0000', label: 'None' },
      { key: '1000', label: 'IH' },
      { key: '0100', label: 'IS' },
      { key: '0010', label: 'OF' },
      { key: '0001', label: 'CAI' },
      { key: '1100', label: 'IH+IS' },
      { key: '1010', label: 'IH+OF' },
      { key: '0011', label: 'OF+CAI' },
      { key: '1111', label: 'All' },
    ];

    // Build header
    thead.innerHTML = '<th>Technique</th>';
    for (const cfg of matrixConfigs) {
      const th = document.createElement('th');
      th.textContent = cfg.label;
      thead.appendChild(th);
    }

    // Build rows
    tbody.innerHTML = '';
    for (const technique of TECHNIQUES) {
      const tr = document.createElement('tr');
      const tdName = document.createElement('td');
      tdName.textContent = technique.title;
      tr.appendChild(tdName);

      for (const cfg of matrixConfigs) {
        const td = document.createElement('td');
        const result = EFFECTIVENESS_TABLE[technique.id]?.[cfg.key];
        if (result) {
          td.textContent = `${result.score}`;
          if (result.score >= 50) {
            td.classList.add('matrix-cell-high');
          } else if (result.score >= 20) {
            td.classList.add('matrix-cell-medium');
          } else {
            td.classList.add('matrix-cell-low');
          }
        }
        tr.appendChild(td);
      }

      tbody.appendChild(tr);
    }
  }

  // Keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    if (shouldIgnoreGlobalShortcutTarget(e.target)) return;

    const action = getShortcutAction(e.key, TECHNIQUES.length);
    if (!action) return;
    e.preventDefault();

    if (action.type === 'select-technique') {
      selectTechnique(TECHNIQUES[action.index].id);
    } else if (action.type === 'run-test') {
      runTestBtn?.click();
    } else {
      resetTestBtn?.click();
    }
  });

  // Populate example payloads on technique cards
  function populatePayloads() {
    techniqueCards.forEach((card) => {
      const id = card.getAttribute('data-technique');
      const technique = TECHNIQUES.find((t) => t.id === id);
      const code = card.querySelector('.technique-payload');
      if (technique && code) {
        code.textContent = technique.examplePayload;
      }
    });
  }

  // Initialize
  updateDefenseStatus();
  populatePayloads();
  populateMatrix();
}
